import { createRouter, createWebHistory } from "vue-router";
import Emanta from "../pages/web/Emanta.vue";
import Index from "@/pages/admin/DashboardIndex.vue";
import DashboardContent from "@/pages/admin/components/DashboardContent.vue";
import ProviderPage from "@/pages/admin/child-pages/Provider/ProviderPage.vue";
import ProviderCatalogIndex from "@/pages/admin/child-pages/ProviderCatalog/ProviderCatalogIndex.vue";
import ServiceCatalogIndex from "@/pages/admin/child-pages/ServiceCatalog/ServiceCatalogIndex.vue";
import CategoryIndex from "@/pages/admin/child-pages/Category/CategoryIndex.vue";
import TagsIndex from "@/pages/admin/child-pages/Tags/TagsIndex.vue";
import ServiceIndex from "@/pages/admin/child-pages/Services/ServiceIndex.vue";
import PackageIndex from "@/pages/admin/child-pages/Packages/PackageIndex.vue";
import CustomerIndex from "@/pages/admin/child-pages/Customer/CustomerIndex.vue";
import ItineraryIndex from "@/pages/admin/child-pages/Itinerary/ItineraryIndex.vue";
import SettingLayout from "@/pages/admin/child-pages/Settings/SettingLayout.vue";
import Login from "@/pages/admin/Login.vue";
import PasswordReset from "@/pages/admin/PasswordReset.vue";
import OrganizationSelector from "@/pages/admin/OrganizationSelector.vue";
import OrganizationsIndex from "@/pages/admin/child-pages/Organizations/OrganizationsIndex.vue";
import PlatformAdminsIndex from "@/pages/admin/child-pages/PlatformAdmins/PlatformAdminsIndex.vue";
import GlobalBillingIndex from "@/pages/admin/child-pages/GlobalBilling/GlobalBillingIndex.vue";
import GlobalPaymentsIndex from "@/pages/admin/child-pages/GlobalPayments/GlobalPaymentsIndex.vue";
import PaymentsIndex from "@/pages/admin/child-pages/Payments/PaymentsIndex.vue";
import UsersIndex from "@/pages/admin/child-pages/Settings/child-pages/Users/UsersIndex.vue";
import RoleIndex from "@/pages/admin/child-pages/Settings/child-pages/Roles/RoleIndex.vue";
import BankAccountsIndex from "@/pages/admin/child-pages/Settings/child-pages/BankAccounts/BankAccountsIndex.vue";
import WidgetKeysIndex from "@/pages/admin/child-pages/Settings/child-pages/WidgetKeys/WidgetKeysIndex.vue";
import BrandThemeIndex from "@/pages/admin/child-pages/Settings/child-pages/BrandTheme/BrandThemeIndex.vue";
import SubscriptionIndex from "@/pages/admin/child-pages/Settings/child-pages/Subscription/SubscriptionIndex.vue";
import OrganizationProfileIndex from "@/pages/admin/child-pages/Settings/child-pages/OrganizationProfile/OrganizationProfileIndex.vue";
import ProfileLayout from "@/pages/admin/child-pages/Settings/child-pages/Profile/ProfileLayout.vue";
import CalendarIndex from "@/pages/admin/child-pages/Calendars/CalendarIndex.vue";
import ConfigurationIndex from "@/pages/admin/child-pages/Settings/child-pages/Configurations/ConfigurationsIndex.vue";
import NotificationPreferencesIndex from "@/pages/admin/child-pages/Settings/child-pages/NotificationPreferences/NotificationPreferencesIndex.vue";
import NotificationDeliveriesIndex from "@/pages/admin/child-pages/Settings/child-pages/NotificationDeliveries/NotificationDeliveriesIndex.vue";
import ReservationsIndex from "@/pages/admin/child-pages/Reservations/ReservationsIndex.vue";
import ConversationsIndex from "@/pages/admin/child-pages/Conversations/ConversationsIndex.vue";
import NotificationsIndex from "@/pages/admin/child-pages/Notifications/NotificationsIndex.vue";
import ItineraryReview from "@/pages/public/ItineraryReview.vue";
import FileDownload from "@/pages/public/FileDownload.vue";
import ReviewPage from "@/pages/public/ReviewPage.vue";
import ReviewsIndex from "@/pages/admin/child-pages/Reviews/ReviewsIndex.vue";
import ChargesIndex from "@/domains/charges/components/ChargesIndex.vue";
import { appAbility, useAuthStore } from "@/domains/auth";
import { useFeatureFlagStore } from "@/domains/feature-flags";
import { shouldRedirectSingleOrgSelector } from "@/domains/auth/services/auth-storage.service";
import { resetAuthSession } from "@/domains/auth/services/auth-session.service";
import { FEATURE_FLAGS } from "@/constants/feature-flags";

const routes = [
  { path: "", component: Emanta },
  {
    path: "/select-organization",
    component: OrganizationSelector,
    meta: { requiresAuth: true, allowNoOrg: true },
  },
  {
    path: "/admin",
    component: Index,
    meta: { requiresAuth: true, routeException: "admin_shell" },
    children: [
      {
        path: "",
        component: DashboardContent,
        meta: { permissions: ["dashboard_view"] },
      },
      {
        path: "global/organizations",
        component: OrganizationsIndex,
        meta: {
          permissions: ["organization_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/admins",
        component: PlatformAdminsIndex,
        meta: {
          permissions: ["platform_user_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/configuration",
        component: ConfigurationIndex,
        meta: {
          permissions: ["configuration_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/categories",
        component: CategoryIndex,
        meta: {
          permissions: ["category_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/reviews",
        component: ReviewsIndex,
        meta: {
          permissions: ["review_view"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/charges",
        component: ChargesIndex,
        meta: {
          permissions: ["charge_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
        },
      },
      {
        path: "global/billing",
        component: GlobalBillingIndex,
        meta: {
          permissions: ["subscription_panel"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
          featureFlagGlobal: FEATURE_FLAGS.ORGANIZATION_BILLING,
        },
      },
      {
        path: "global/billing/payments",
        component: GlobalPaymentsIndex,
        meta: {
          permissions: ["payment_operations_manage"],
          allowNoOrg: true,
          globalAdmin: true,
          superAdminOnly: true,
          featureFlagGlobal: FEATURE_FLAGS.ITINERARY_ONVO_PAYMENTS,
        },
      },
      {
        path: "provider",
        component: ProviderPage,
        meta: { permissions: ["total_access", "provider_manage"] },
      },
      {
        path: "catalog/providers",
        component: ProviderCatalogIndex,
        meta: {
          permissions: ["provider_catalog_view"],
          allowedOrgTypes: ["concierge"],
        },
      },
      {
        path: "catalog/services",
        component: ServiceCatalogIndex,
        meta: {
          permissions: ["service_catalog_view"],
          allowedOrgTypes: ["concierge"],
        },
      },
      {
        path: "reservas",
        component: ReservationsIndex,
        meta: { permissions: ["total_access", "reservations_view"] },
      },
      {
        path: "service",
        component: ServiceIndex,
        meta: {
          permissions: ["total_access", "service_view"],
          allowedOrgTypes: ["provider", "concierge"],
        },
      },
      {
        path: "package",
        component: PackageIndex,
        meta: { permissions: ["total_access", "package_view"] },
      },
      {
        path: "calendar",
        component: CalendarIndex,
        meta: { permissions: ["total_access", "calendar_view"] },
      },
      {
        path: "customer",
        component: CustomerIndex,
        meta: { permissions: ["total_access", "customer_view"] },
      },
      {
        path: "itinerary",
        component: ItineraryIndex,
        meta: { permissions: ["total_access", "itinerary_view"] },
      },
      {
        path: "conversations",
        component: ConversationsIndex,
        meta: { permissions: ["total_access", "conversation_view"] },
      },
      {
        path: "notifications",
        component: NotificationsIndex,
        meta: {
          permissions: ["notification_view"],
          featureFlag: FEATURE_FLAGS.NOTIFICATIONS,
        },
      },
      {
        path: "payments",
        component: PaymentsIndex,
        meta: {
          permissions: ["payment_operations_view"],
          allowedOrgTypes: ["provider", "concierge"],
          featureFlag: FEATURE_FLAGS.ITINERARY_ONVO_PAYMENTS,
        },
      },
      {
        path: "reviews",
        component: ReviewsIndex,
        meta: {
          permissions: ["review_view"],
          allowedOrgTypes: ["provider", "concierge"],
          featureFlag: FEATURE_FLAGS.REVIEWS,
        },
      },
      // {
      //   path: "itinerary-form",
      //   component: LayoutForm
      // },
      {
        path: "settings",
        component: SettingLayout,
        meta: { routeException: "settings_shell" },
        children: [
          {
            path: "organizations",
            component: OrganizationsIndex,
            meta: {
              permissions: ["organization_manage"],
              allowNoOrg: true,
              globalAdmin: true,
              superAdminOnly: true,
            },
          },
          {
            path: "profile",
            component: ProfileLayout,
            meta: {
              routeException: "self_profile",
              hideForSuperAdminTenant: true,
            },
          },
          {
            path: "organization-profile",
            component: OrganizationProfileIndex,
            meta: {
              routeException: "organization_profile",
              orgProfileOnly: true,
            },
          },
          {
            path: "users",
            component: UsersIndex,
            meta: { permissions: ["member_manage"] },
          },
          {
            path: "bank-accounts",
            component: BankAccountsIndex,
            meta: {
              permissions: ["organization_bank_account_manage"],
            },
          },
          {
            path: "widget-keys",
            component: WidgetKeysIndex,
            meta: { permissions: ["organization_manage"] },
          },
          {
            path: "brand-theme",
            component: BrandThemeIndex,
            meta: {
              permissions: ["organization_brand_theme_manage"],
              allowedOrgTypes: ["concierge"],
            },
          },
          {
            path: "subscription",
            component: SubscriptionIndex,
            meta: {
              permissions: ["organization_billing_summary"],
              featureFlag: FEATURE_FLAGS.ORGANIZATION_BILLING,
            },
          },
          {
            path: "notification-preferences",
            component: NotificationPreferencesIndex,
            meta: {
              permissions: ["notification_manage"],
              featureFlag: FEATURE_FLAGS.NOTIFICATIONS,
            },
          },
          {
            path: "notification-deliveries",
            component: NotificationDeliveriesIndex,
            meta: {
              permissions: ["notification_manage"],
              allowNoOrg: true,
              globalAdmin: true,
              superAdminOnly: true,
              featureFlagGlobal: FEATURE_FLAGS.NOTIFICATIONS,
            },
          },
          {
            path: "pdf-theme",
            redirect: "/admin/settings/brand-theme",
            meta: { routeException: "redirect" },
          },
          {
            path: "roles",
            component: RoleIndex,
            meta: { permissions: ["role_access_read"] },
          },
          {
            path: "category",
            redirect: () => getDefaultSettingsRoute(),
            meta: { routeException: "redirect" },
          },
          {
            path: "tag",
            component: TagsIndex,
            meta: { permissions: ["total_access", "tag_manage"] },
          },
          {
            path: "general-config",
            component: ConfigurationIndex,
            meta: { permissions: ["total_access"] },
          },
          {
            path: "",
            redirect: () => getDefaultSettingsRoute(),
            meta: { routeException: "redirect" },
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    component: Login,
  },
  {
    path: "/password-reset",
    component: PasswordReset,
  },
  {
    path: "/password-recoveries/:recoveryId",
    component: PasswordReset,
  },
  {
    path: "/itineraries/:itineraryId/approvals/:approvalGrantId",
    component: ItineraryReview,
    meta: { routeException: "public_itinerary_review" },
  },
  {
    path: "/files/:fileId/download",
    component: FileDownload,
    meta: { routeException: "public_file_download" },
  },
  {
    path: "/review-invitations/:invitationId",
    component: ReviewPage,
    meta: { routeException: "public_review" },
  },
  { path: "/**", component: Emanta },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const PERMISSION_ABILITY_MAP = {
  total_access: { action: "manage", subject: "all" },
  provider_view: { action: "read", subject: "Provider" },
  provider_manage: { action: "manage", subject: "Provider" },
  provider_catalog_view: { action: "read", subject: "ProviderCatalog" },
  service_catalog_view: { action: "read", subject: "Service" },
  service_view: { action: "read", subject: "Service" },
  calendar_view: { action: "read", subject: "Calendar" },
  customer_view: { action: "read", subject: "Customer" },
  itinerary_view: { action: "read", subject: "Itinerary" },
  reservations_view: { action: "read", subject: "Reservation" },
  conversation_view: { action: "read", subject: "Conversation" },
  notification_view: { action: "read", subject: "Notification" },
  notification_manage: { action: "manage", subject: "Notification" },
  review_view: { action: "read", subject: "Review" },
  dashboard_view: { action: "read", subject: "Dashboard" },
  organization_manage: { action: "manage", subject: "Organization" },
  organization_bank_account_read: {
    action: "read",
    subject: "OrganizationBankAccount",
  },
  organization_bank_account_manage: {
    action: "manage",
    subject: "OrganizationBankAccount",
  },
  organization_brand_theme_manage: { action: "read", subject: "Organization" },
  organization_billing_summary: {
    action: "read",
    subject: "OrganizationBilling",
  },
  subscription_panel: { action: "manage", subject: "OrganizationBilling" },
  payment_operations_view: { action: "read", subject: "PaymentOperations" },
  payment_operations_manage: { action: "manage", subject: "PaymentOperations" },
  platform_user_manage: { action: "manage", subject: "User" },
  member_manage: { action: "manage", subject: "OrganizationMember" },
  user_manage: { action: "manage", subject: "OrganizationMember" },
  role_access_read: { action: "read", subject: "Role" },
  configuration_manage: { action: "manage", subject: "System" },
  category_manage: { action: "manage", subject: "Category" },
  tag_manage: { action: "manage", subject: "Tag" },
  package_view: { action: "read", subject: "Package" },
  feature_flag_manage: { action: "manage", subject: "FeatureFlag" },
  charge_manage: { action: "manage", subject: "ChargeTemplate" },
};

function isSuperAdminTenantContext() {
  const authStore = useAuthStore();
  return authStore.isSuperAdmin && !!authStore.currentOrgId;
}

function getDefaultSettingsRoute() {
  return isSuperAdminTenantContext()
    ? "/admin/settings/organization-profile"
    : "/admin/settings/profile";
}

function canAccessOrganizationProfile() {
  const authStore = useAuthStore();
  const roleKey =
    authStore.primaryRole || localStorage.getItem("role_key") || "";
  const hasTenantContext = !!(
    authStore.currentOrgId || localStorage.getItem("org_id")
  );

  if (!hasTenantContext) return false;
  if (isSuperAdminTenantContext()) return true;

  return (
    roleKey === "provider_owner" ||
    roleKey === "provider_admin" ||
    roleKey === "concierge_owner" ||
    roleKey === "concierge_admin"
  );
}

function getCurrentOrgType() {
  const authStore = useAuthStore();
  if (authStore.currentOrgType) return authStore.currentOrgType;

  const roleKey = localStorage.getItem("role_key") || "";
  if (roleKey.startsWith("provider")) return "provider";
  if (roleKey.startsWith("concierge")) return "concierge";
  return null;
}

async function canAccessFeatureFlaggedRoute(to) {
  if (to.meta.featureFlagGlobal) {
    const featureFlagStore = useFeatureFlagStore();
    await featureFlagStore.loadGlobal();
    return featureFlagStore.isGlobalEnabled(to.meta.featureFlagGlobal);
  }

  if (to.meta.featureFlag) {
    const featureFlagStore = useFeatureFlagStore();
    await featureFlagStore.loadRuntime(localStorage.getItem("org_id"));
    return featureFlagStore.isRuntimeEnabled(to.meta.featureFlag);
  }

  return true;
}

// Guard de navegación
router.beforeEach(async (to, from, next) => {
  if (to.meta.routeException === "public_itinerary_review") return next();
  const authStore = useAuthStore();

  if (to.meta.requiresAuth) {
    await authStore.initialize();
    if (!authStore.isAuthenticated) {
      if (authStore.sessionState !== "error") resetAuthSession();
      return next("/login");
    }
  }

  const isAuthenticated = authStore.isAuthenticated;
  const hasOrgContext = !!localStorage.getItem("org_id");
  const isOrgSelectionRoute = to.path === "/select-organization";
  const isGlobalPlatformOwner =
    localStorage.getItem("role_key") === "platform_owner" && !hasOrgContext;

  if (isAuthenticated && to.meta.superAdminOnly && !isGlobalPlatformOwner) {
    return next("/admin");
  }

  if (
    isAuthenticated &&
    to.meta.hideForSuperAdminTenant &&
    isSuperAdminTenantContext()
  ) {
    return next("/admin/settings/organization-profile");
  }

  if (isAuthenticated && to.meta.orgProfileOnly && !canAccessOrganizationProfile()) {
    return next("/admin");
  }

  if (
    isAuthenticated &&
    to.meta.requiresAuth &&
    !to.meta.allowNoOrg &&
    !hasOrgContext
  ) {
    return next("/select-organization");
  }

  if (isAuthenticated && to.meta.allowedOrgTypes) {
    const currentOrgType = getCurrentOrgType();
    if (!to.meta.allowedOrgTypes.includes(currentOrgType)) {
      return next("/admin");
    }
  }

  if (isAuthenticated && isOrgSelectionRoute && hasOrgContext) {
    if (shouldRedirectSingleOrgSelector()) {
      return next("/admin");
    }
  }

  if (isAuthenticated && !(await canAccessFeatureFlaggedRoute(to))) {
    return next("/admin");
  }

  if (to.meta.permissions) {
    const hasPermission = to.meta.permissions.some((permission) => {
      const check = PERMISSION_ABILITY_MAP[permission];
      if (!check) return false;
      return appAbility.can(check.action, check.subject);
    });

    if (!hasPermission) {
      return next("/admin");
    }
  }

  next();
});

export default router;
